import {
  VMPricingRow, DiskPricingRow, AddonPricingRow,
  AddonOptionPricingRow, DatabasePricingRow, DatabaseEditionPricingRow,
  PowerVMPricingRow, PowerAddonPricingRow, PowerAddonOptionPricingRow,
} from '../../types';
import { VMRow, DatabaseRow, OtherLineItem, PowerVMRow, PowerAddonLineItem, SelectOption } from './types';

interface UseAvailabilityParams {
  vmPricing: Record<string, VMPricingRow>;
  diskPricing: Record<string, DiskPricingRow>;
  addonPricing: Record<string, AddonPricingRow>;
  addonOptionPricing: Record<string, AddonOptionPricingRow>;
  databasePricing: Record<string, DatabasePricingRow>;
  databaseVMPricing: Record<string, DatabaseEditionPricingRow>;
  selectedRegion: number | string;
  pricingReady: boolean;
  getVMTypeId: (cpu: number, ram: number) => number | undefined;
  getOSCategoryById: (osTypeId: number | string) => string | null;
  powerVMPricing: Record<string, PowerVMPricingRow>;
  powerAddonPricing: Record<string, PowerAddonPricingRow>;
  powerAddonOptionPricing: Record<string, PowerAddonOptionPricingRow>;
}

export const useAvailability = ({
  vmPricing, diskPricing, addonPricing, addonOptionPricing,
  databasePricing, databaseVMPricing,
  selectedRegion, pricingReady,
  getVMTypeId, getOSCategoryById,
  powerVMPricing, powerAddonPricing, powerAddonOptionPricing,
}: UseAvailabilityParams) => {

  const isVMRowSelected = (vm: VMRow) =>
    Number.isFinite(vm.cpu) && Number.isFinite(vm.ram) && vm.osTypeId !== '' && vm.osTypeId !== null;

  const isVMRowAvailable = (vm: VMRow) => {
    if (vm.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!isVMRowSelected(vm)) return true;
    const vmTypeId = getVMTypeId(vm.cpu, vm.ram);
    if (!vmTypeId) return false;
    const cat = getOSCategoryById(vm.osTypeId);
    const base = vmPricing[`${vmTypeId}-${cat}-${selectedRegion}`];
    if (!base?.IsAvailable) return false;
    return (vm.disks ?? []).every((d) => !d || diskPricing[`${d}-${selectedRegion}`]?.IsAvailable);
  };

  const isPaasDbRowSelected = (db: DatabaseRow) =>
    !!db.databaseTypeId && db.deploymentType === 'paas' && Number.isFinite(db.cores) && (db.tier || db.databaseConfigId);

  const isPaasDbRowAvailable = (db: DatabaseRow) => {
    if (db.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!isPaasDbRowSelected(db)) return true;
    if (db.deploymentType !== 'paas') return true;
    return !!databasePricing[`${db.databaseConfigId}-${selectedRegion}`]?.IsAvailable;
  };

  const isVmDbRowSelected = (db: DatabaseRow) =>
    db.deploymentType === 'vm_based' && Number.isFinite(db.cpu) && Number.isFinite(db.ram) && db.editionId !== '' && db.editionId !== null;

  const isVmDbRowAvailable = (db: DatabaseRow) => {
    if (db.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!isVmDbRowSelected(db)) return true;
    if (db.deploymentType !== 'vm_based') return true;
    const vmTypeId = getVMTypeId(db.cpu, db.ram);
    if (!vmTypeId) return false;
    const base = databaseVMPricing[`${vmTypeId}-${db.editionId}-${selectedRegion}`];
    if (!base?.IsAvailable) return false;
    return (db.disks ?? []).every((d) => !d || diskPricing[`${d}-${selectedRegion}`]?.IsAvailable);
  };

  const isAddonRowSelected = (item: OtherLineItem) =>
    !!parseInt(String(item.itemId), 10);

  const isAddonRowAvailable = (item: OtherLineItem) => {
    if (item.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!isAddonRowSelected(item)) return true;
    if (item.optionId) {
      return !!addonOptionPricing[`${parseInt(String(item.optionId), 10)}-${selectedRegion}`]?.IsAvailable;
    }
    return !!addonPricing[`${parseInt(String(item.itemId), 10)}-${selectedRegion}`]?.IsAvailable;
  };

  const ensureCurrentOption = (options: SelectOption[], currentValue: number | string | null | undefined, makeLabel: (v: number | string) => string): SelectOption[] => {
    const hasCurrent = options.some((opt) => String(opt.value) === String(currentValue));
    if (currentValue === '' || currentValue === null || currentValue === undefined || hasCurrent) return options;
    return [{ value: currentValue, label: `${makeLabel(currentValue)} (Unavailable)`, disabled: true }, ...options];
  };

  const isPowerVMRowAvailable = (row: PowerVMRow) => {
    if (row.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!row.powerTypeId) return true;
    const key = `${row.powerTypeId}-${selectedRegion}`;
    const p = powerVMPricing[key];
    if (!p?.IsAvailable) return false;
    return (row.disks ?? []).every((d) => !d || diskPricing[`${d}-${selectedRegion}`]?.IsAvailable);
  };

  const isPowerAddonRowAvailable = (item: PowerAddonLineItem) => {
    if (item.savedLineCost != null) return true;
    if (!pricingReady) return true;
    if (!item.powerAddonId) return true;
    if (item.powerAddonOptionId) {
      return !!powerAddonOptionPricing[`${parseInt(String(item.powerAddonOptionId), 10)}-${selectedRegion}`]?.IsAvailable;
    }
    return !!powerAddonPricing[`${parseInt(String(item.powerAddonId), 10)}-${selectedRegion}`]?.IsAvailable;
  };

  return {
    isVMRowSelected, isVMRowAvailable,
    isPaasDbRowSelected, isPaasDbRowAvailable,
    isVmDbRowSelected, isVmDbRowAvailable,
    isAddonRowSelected, isAddonRowAvailable,
    isPowerVMRowAvailable, isPowerAddonRowAvailable,
    ensureCurrentOption,
  };
};
